/**
 * BVH-accelerated raycasting, registered globally for every `THREE.Mesh` /
 * `THREE.BufferGeometry` in the app.
 *
 * WHY THIS EXISTS
 * ----------------
 * Three.js's default `Mesh.raycast` does a linear scan of every triangle in
 * a geometry. A LandXML TIN surface can be tens of millions of triangles
 * (see `landxmlLod.ts`), and several viewer tools — Measure Distance/Area,
 * Elevation Profile, Stockpile, Pipe, Crest Guide (`pickSurface.ts`) —
 * raycast on every raw `mousemove` while active. `LandXML.tsx`'s `LodSurface`
 * deliberately keeps picking on the FULL-resolution geometry, never the
 * coarse LOD level, so measurements stay accurate (see the comment there) —
 * which meant that linear scan ran on every mouse move. On a huge TIN that
 * is 100s of ms to seconds per event: the tab visibly freezes the moment a
 * picking tool goes active over the model.
 *
 * `three-mesh-bvh` builds a bounding-volume hierarchy once per geometry and
 * turns that O(triangles) scan into O(log n), with IDENTICAL hit results —
 * it changes nothing about picking accuracy, only how fast a hit is found.
 * `LandXML.tsx` calls `geometry.computeBoundsTree()` once per surface right
 * after it's built (the same load-time cost class as the LOD build already
 * paid there) and `geometry.disposeBoundsTree()` when the geometry is
 * disposed.
 *
 * Imported once, for this side effect, from `main.tsx` — before any
 * component ever raycasts. The patch is global (every mesh in the scene, not
 * just LandXML) because `Mesh.prototype.raycast` has exactly one slot; a
 * geometry with no bounds tree built just falls through to the same
 * (correct) linear-scan behaviour `acceleratedRaycast` already has as its
 * fallback, so this is safe for every other mesh in the viewer.
 */
import * as THREE from 'three';
import { acceleratedRaycast, computeBoundsTree, disposeBoundsTree } from 'three-mesh-bvh';

THREE.BufferGeometry.prototype.computeBoundsTree = computeBoundsTree;
THREE.BufferGeometry.prototype.disposeBoundsTree = disposeBoundsTree;
THREE.Mesh.prototype.raycast = acceleratedRaycast;
